import { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { Save, AlertCircle, BarChart2, Facebook, Camera, Music, X, Plus } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = {
  google: { title: "Google Analytics", color: "bg-red-800", borderColor: "border-red-200", icon: BarChart2 },
  facebook: { title: "Pixel Facebook", color: "bg-blue-900", borderColor: "border-blue-200", icon: Facebook },
  snapchat: { title: "Pixel Snapchat", color: "bg-yellow-500", borderColor: "border-yellow-200", icon: Camera },
  tiktok: { title: "Pixel TikTok", color: "bg-black", borderColor: "border-neutral-200", icon: Music },
};

const CONVERSION_TYPES = {
  google: ["Page view", "Purchase", "Sign up", "Lead"],
  facebook: ["Purchase", "Lead", "Complete registration", "Add to cart"],
  snapchat: ["Purchase", "Sign up", "Add to cart", "View content"],
  tiktok: ["View content", "Add to cart", "Purchase", "Generate lead"],
};

const PixelSettings = () => {
  const [settings, setSettings] = useState(() =>
    Object.keys(PLATFORMS).reduce((acc, platform) => {
      acc[platform] = { pixels: [], type: "" };
      return acc;
    }, {})
  );
  const [currentPixel, setCurrentPixel] = useState(() =>
    Object.keys(PLATFORMS).reduce((acc, platform) => {
      acc[platform] = "";
      return acc;
    }, {})
  );
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Object.keys(PLATFORMS).forEach(async (platform) => {
      try {
        const response = await axiosInstance.post(`/pixels/search`, { platform });
        if (response.data) {
          setSettings((prev) => ({
            ...prev,
            [platform]: {
              pixels: response.data.pixel_id || [],
              type: response.data.conversion_type || "",
            },
          }));
        }
      } catch (error) {
        console.error(`Error fetching data for ${platform}:`, error);
      }
    });
  }, []);

  const handleChange = (platform, field, value) => {
    if (field === "pixel") {
      setCurrentPixel((prev) => ({ ...prev, [platform]: value }));
    } else {
      setSettings((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], [field]: value },
      }));
    }
    setErrors((prev) => ({ ...prev, [platform]: null }));
  };

  const addPixel = (platform) => {
    const pixel = currentPixel[platform].trim();
    if (!pixel) {
      setErrors((prev) => ({ ...prev, [platform]: "L'ID du pixel est requis" }));
      return;
    }
    setSettings((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], pixels: [...prev[platform].pixels, pixel] },
    }));
    setCurrentPixel((prev) => ({ ...prev, [platform]: "" }));
  };

  const removePixel = (platform, pixelToRemove) => {
    setSettings((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        pixels: prev[platform].pixels.filter((pixel) => pixel !== pixelToRemove),
      },
    }));
  };

  const handleSave = async (platform) => {
    try {
      const payload = {
        platform,
        pixel_id: settings[platform].pixels,
        conversion_type: settings[platform].type,
      };
      const response = await axiosInstance.post("/pixels/store", payload);
      if (response.data) {
        toast.success(`Paramètres ${platform} sauvegardés`);
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement des paramètres");
    }
  };

  const handleEmptyPixel = async (platform) => {
    try {
      const response = await axiosInstance.post("/pixels/emptyPixel", { platform });
      if (response.data) {
        setSettings((prev) => ({
          ...prev,
          [platform]: { pixels: [], type: "" },
        }));
        toast.success(`Pixels pour ${platform} vidés`);
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression des pixels");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      {Object.entries(PLATFORMS).map(([platform, { title, color, borderColor, icon: Icon }]) => (
        <div key={platform} className={`mb-4 border rounded-lg overflow-hidden ${borderColor}`}>
          <div className={`${color} p-4 flex justify-between items-center`}>
            <div className="flex items-center gap-2">
              <Icon className="w-6 h-6 text-white" />
              <h2 className="text-white font-medium">{title}</h2>
            </div>
          </div>

          <div className="p-4 bg-white space-y-4">
            <div>
              <label className="block text-start mb-2 text-sm font-medium text-neutral-700">
                {platform === "google" ? "Identifiants de suivi Google" : `Pixel ${title}`}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentPixel[platform]}
                  onChange={(e) => handleChange(platform, "pixel", e.target.value)}
                  className={`flex-grow p-2 h-10 border text-sm rounded-md ${errors[platform] ? "border-red-500" : "border-neutral-300"
                    }`}
                  placeholder="ID du pixel"
                />
                <button
                  onClick={() => addPixel(platform)}
                  className="px-3 py-2 bg-black text-white rounded-md hover:bg-blue-700 transition"
                >
                  <Plus size={17} />
                </button>
              </div>
              {errors[platform] && (
                <div className="mt-1 text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors[platform]}
                </div>
              )}
            </div>

            {settings[platform].pixels.length > 0 && (
              <div className="mt-2">
                <h3 className="text-sm text-start mb-2 font-medium text-neutral-700">Pixels ajoutés :</h3>
                <div className="flex flex-wrap gap-2">
                  {settings[platform].pixels.map((pixel, index) => (
                    <div key={index} className="flex justify-between items-center bg-neutral-100 border border-neutral-200 w-full rounded-lg px-3 py-2">
                      <span className="text-sm text-neutral-800">{pixel}</span>
                      <button
                        onClick={() => removePixel(platform, pixel)}
                        className="ml-2 text-neutral-500 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-start mb-2 text-sm font-medium text-neutral-700">Type de conversion</label>
              <select
                value={settings[platform].type}
                onChange={(e) => handleChange(platform, "type", e.target.value)}
                className="w-full p-2 h-10 border border-neutral-300 rounded-md text-sm"
              >
                <option value="">Sélectionnez un type</option>
                {CONVERSION_TYPES[platform].map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => handleSave(platform)}
                className="flex items-center gap-2 px-4 py-2 bg-neutral-100 border border-neutral-200 text-neutral-800 text-sm rounded-md transition"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
              <button
                onClick={() => handleEmptyPixel(platform)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-md transition"
              >
                Vider les pixels
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PixelSettings;
